import type { BillingSummary, BillingSummaryWithSeats } from "../api/contract";
import { TEAM_SEAT_LIMIT } from "./offers";

/**
 * Normalize `GET /v1/orgs/{orgId}/billing` for the seat-aware Billing UI.
 *
 * The four Team-rollout fields (`billingInterval`, `maxSeats`, `grandfatheredSeatLimit`,
 * `canChangeSeatQuantity`) are optional in the shared contract while API instances roll
 * over, so a summary from an older instance may omit any of them. The UI must never
 * render `undefined`, `NaN`, or an invented capability from that:
 *
 * - `billingInterval` → `null` unless the API says "month" or "year";
 * - `maxSeats` → the plan ceiling: Team 50; Pro its grandfathered ceiling, else its
 *   licensed seats (never below one); everything else its licensed seats;
 * - `grandfatheredSeatLimit` → `null` unless supplied as a positive whole number;
 * - `canChangeSeatQuantity` → `false` unless the API says `true`.
 *
 * `licensedSeats` is also coerced to a finite non-negative integer so the seat tiles and
 * bounds never compute with `NaN`.
 */
export function normalizeBillingSummary(raw: BillingSummary): BillingSummaryWithSeats {
  const source = raw as BillingSummary & Partial<BillingSummaryWithSeats>;
  const licensedSeats = wholeNumber(source.licensedSeats, 0);
  const grandfatheredSeatLimit = positiveWholeNumberOrNull(source.grandfatheredSeatLimit);
  const suppliedMax = positiveWholeNumberOrNull(source.maxSeats);
  const accessLevel = String(source.accessLevel ?? "").toLowerCase();
  const maxSeats =
    suppliedMax ??
    (accessLevel === "team"
      ? TEAM_SEAT_LIMIT
      : accessLevel === "pro"
        ? Math.max(1, grandfatheredSeatLimit ?? licensedSeats)
        : Math.max(1, licensedSeats));
  return {
    ...source,
    licensedSeats,
    billingInterval: source.billingInterval === "month" || source.billingInterval === "year" ? source.billingInterval : null,
    maxSeats,
    grandfatheredSeatLimit,
    canChangeSeatQuantity: source.canChangeSeatQuantity === true
  };
}

function wholeNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;
}

function positiveWholeNumberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 1 ? Math.floor(value) : null;
}
